import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Menu, X, User, LogOut, Settings, Search, Briefcase, 
  BookOpen, Building2, Users, ChevronDown
} from 'lucide-react';
import Logo from './logo';
import { getSidebarItemsForRole } from './SidebarPresets';
import { API_BASE_URL } from '../config/apiconfig';


const Header = () => {
  const { user, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminMobileOpen, setAdminMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [recruiterApproval, setRecruiterApproval] = useState('approved');
  const [recruiterAccountStatus, setRecruiterAccountStatus] = useState('active');
  const [recruiterImageUrl, setRecruiterImageUrl] = useState(null);
  const navigate = useNavigate();
  // Mobile recruiter submenu state
  const [recHireOpen, setRecHireOpen] = useState(false);
  const [recRetainOpen, setRecRetainOpen] = useState(false);
  const [recSettingsOpen, setRecSettingsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch display name for non-admins; for admin roles use their email
  useEffect(() => {
    const fetchProfileName = async () => {
      if (!user?.email) {
        setDisplayName("");
        return;
      }
      // Admins (e2e-admin, company-admin, cluster-admin, unit-admin): show their email
      if (user?.role === 'e2e-admin' || user?.role === 'company-admin' || user?.role === 'cluster-admin' || user?.role === 'unit-admin') {
        setDisplayName(user.email || "");
        return;
      }
      try {
        // Recruiters store names in recruiters table with snake_case fields
        if (user?.role === 'recruiter' && user?.id) {
          const rRes = await fetch(`${API_BASE_URL}/api/recruiter/profile/${encodeURIComponent(user.id)}`);
          if (!rRes.ok) {
            setDisplayName("");
            return;
          }
          const rProfile = await rRes.json();
          setRecruiterApproval(rProfile?.approval_status || 'pending');
          setRecruiterAccountStatus(rProfile?.account_status || 'inactive');
          const first = rProfile?.first_name?.trim?.() || "";
          const last = rProfile?.last_name?.trim?.() || "";
          const name = [first, last].filter(Boolean).join(" ");
          setDisplayName(name);
          // Set recruiter profile picture from recruiters table (use serving route with filename).
          // If default is present, still use it so the header shows default.png from recruiter uploads.
          const rel = (rProfile?.profile_picture || '').trim();
          if (rel) {
            const filename = rel.split('/').pop();
            if (filename) setRecruiterImageUrl(`${API_BASE_URL}/api/recruiter/profile-picture/${filename}`);
            else setRecruiterImageUrl(`${API_BASE_URL}/api/recruiter/profile-picture/default.png`);
          } else {
            setRecruiterImageUrl(`${API_BASE_URL}/api/recruiter/profile-picture/default.png`);
          }
          return;
        }

        // Default: regular user profile
        const res = await fetch(`${API_BASE_URL}/api/profile/by-email/${encodeURIComponent(user.email)}`);
        if (!res.ok) {
          setDisplayName("");
          return;
        }
        const profile = await res.json();
        const first = profile?.firstName?.trim?.() || "";
        const last = profile?.lastName?.trim?.() || "";
        const name = [first, last].filter(Boolean).join(" ");
        setDisplayName(name);
      } catch (e) {
        setDisplayName("");
      }
    };
    fetchProfileName();
  }, [user?.email, user?.role, user?.id]);

  const handleLogout = async () => {
    await logout();
    navigate('/home', { replace: true });
  };

  const isAdmin = user?.role === 'e2e-admin';
  const isRecruiter = user?.role === 'recruiter';
  const recruiterBlocked = isRecruiter && (recruiterApproval !== 'approved' || recruiterAccountStatus !== 'active');
  // For company/cluster/unit admins use company logo as avatar
  const isScopeAdmin = user?.role === 'company-admin' || user?.role === 'cluster-admin' || user?.role === 'unit-admin';
  const scopeAdminPath = (() => {
    if (user?.role === 'company-admin') return '/company-admin';
    if (user?.role === 'cluster-admin') return '/cluster-admin';
    if (user?.role === 'unit-admin') return '/unit-admin';
    return null;
  })();
  const isAnyAdmin = isAdmin || isScopeAdmin; // any admin type
  const profileImageSrc = (() => {
    if (isScopeAdmin && user?.companyId) {
      return `${API_BASE_URL}/api/companies/${user.companyId}/logo?${new Date().getTime()}`;
    }
    if (isRecruiter) {
      return recruiterImageUrl || `${API_BASE_URL}/api/recruiter/profile-picture/default.png`;
    }
    if (user?.email) {
      return `${API_BASE_URL}/profile/${user.email}/picture?${new Date().getTime()}`;
    }
    return '/default-avatar.png';
  })();
  // Determine logo target based on role
  const logoTo = (() => {
    if (user?.role === 'e2e-admin') return '/admin-dashboard';
    if (user?.role === 'company-admin') return '/company-admin';
    if (user?.role === 'cluster-admin') return '/cluster-admin';
    if (user?.role === 'unit-admin') return '/unit-admin';
    if (user?.role === 'recruiter') return '/recruiter-dashboard';
    return '/home';
  })();

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-700 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-md'
          : 'bg-gradient-to-r from-white via-slate-50 to-white border-b border-slate-100'
      }`}
    >
      {/* Desktop */}
      <div className="hidden md:flex justify-between items-center p-4 w-full px-6 lg:px-10">
        {/* Logo */}
        <Link to={logoTo} className="flex items-center group">
          <Logo />
        </Link>

        {/* Nav */}
        <nav>
          <ul className="flex space-x-2">
            {!isScopeAdmin && (
              <li>
                <Link
                  to="/job-search"
                  onClick={e=>{ if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } }}
                  className="relative flex items-center px-6 py-3 text-base font-semibold text-slate-700 rounded-xl hover:text-blue-600 transition-all"
                >
                  <Search className="mr-3 w-5 h-5 text-blue-500" />
                  Find Jobs
                </Link>
              </li>
            )}
            {user && !isRecruiter && !isAnyAdmin && (
              <>
                <li>
                  <Link
                    to="/profile"
                    className="relative flex items-center px-6 py-3 text-base font-semibold text-slate-700 rounded-xl hover:text-blue-600 transition-all"
                  >
                    <User className="mr-3 w-5 h-5 text-blue-500" />
                    Profile
                  </Link>
                </li>
                <li>
                  <Link
                    to="/my-applications"
                    className="relative flex items-center px-6 py-3 text-base font-semibold text-slate-700 rounded-xl hover:text-blue-600 transition-all"
                  >
                    <Briefcase className="mr-3 w-5 h-5 text-blue-500" />
                    Applications
                  </Link>
                </li>
              </>
            )}

            {!isScopeAdmin && (
              <li>
                <Link
                  to="/career-advice"
                  onClick={e=>{ if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } }}
                  className="relative flex items-center px-6 py-3 text-base font-semibold text-slate-700 rounded-xl hover:text-blue-600 transition-all"
                >
                  <BookOpen className="mr-3 w-5 h-5 text-blue-500" />
                  Career Hub
                </Link>
              </li>
            )}

            {!isScopeAdmin && (
              <li>
                <Link
                  to="/explore-companies"
                  onClick={e=>{ if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } }}
                  className="relative flex items-center px-6 py-3 text-base font-semibold text-slate-700 rounded-xl hover:text-blue-600 transition-all"
                >
                  <Building2 className="mr-3 w-5 h-5 text-blue-500" />
                  Companies
                </Link>
              </li>
            )}
            {isAdmin && (
              <>
                <li>
                  <Link
                    to="/admin-dashboard"
                    onClick={e=>{ if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } }}
                    className="relative flex items-center px-6 py-3 text-base font-semibold text-slate-700 rounded-xl hover:text-blue-600 transition-all"
                  >
                    <Settings className="mr-3 w-5 h-5 text-blue-500" />
                    Admin
                  </Link>
                </li>
                <li>
                  <Link
                    to="/candidates"
                    onClick={e=>{ if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } }}
                    className="relative flex items-center px-6 py-3 text-base font-semibold text-slate-700 rounded-xl hover:text-blue-600 transition-all"
                  >
                    <Users className="mr-3 w-5 h-5 text-blue-500" />
                    Talent Pool
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>

        {/* User */}
        <div className="flex items-center space-x-4">
          {user ? (
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center space-x-3 px-5 py-3 text-base font-medium bg-white border border-slate-200 rounded-xl shadow-sm hover:border-blue-400 transition-all"
              >
                <img
                  src={profileImageSrc}
                  onError={(e) => (e.target.src = isRecruiter ? `${API_BASE_URL}/api/recruiter/profile-picture/default.png` : '/default-avatar.png')}
                  alt="Profile"
                  className="w-9 h-9 rounded-lg object-cover"
                />
                {displayName && (
                  <span className="text-slate-700">{displayName}</span>
                )}
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${
                    dropdownOpen ? 'rotate-180 text-blue-500' : 'text-slate-500'
                  }`}
                />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-60 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                  {!isRecruiter && !isAnyAdmin && (
                    <button
                      onClick={() => navigate('/profile')}
                      className="w-full text-left px-6 py-4 text-base hover:bg-blue-50 text-slate-700 flex items-center"
                    >
                      <Settings className="mr-3 w-5 h-5 text-blue-500" />
                      Edit Profile
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-6 py-4 text-base hover:bg-red-50 text-red-600 flex items-center"
                  >
                    <LogOut className="mr-3 w-5 h-5" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="relative px-7 py-3.5 text-base font-bold text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-700 via-blue-600 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 animate-pulse"></div>
              <div className="relative flex items-center justify-center">
                <User className="inline-block mr-2 w-5 h-5 group-hover:animate-bounce" />
                Join Now
              </div>
            </Link>
          )}
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden flex items-center justify-between px-7 py-4">
        <Link to={logoTo}>
          <Logo align="left" />
        </Link>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-3 rounded-lg bg-slate-100 border border-slate-200"
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6 text-slate-700" />
          ) : (
            <Menu className="w-6 h-6 text-slate-700" />
          )}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-md">
          <nav className="p-4">
            <ul className="space-y-2">
              {!isScopeAdmin && (
                <li>
                  <Link
                    to="/job-search"
                    onClick={e=>{ if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); setMobileMenuOpen(false);} }}
                    className="block px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                  >
                    Find Jobs
                  </Link>
                </li>
              )}
              {user && !isRecruiter && !isAnyAdmin && (
                <>
                  <li>
                    <Link
                      to="/profile"
                      className="block px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                    >
                      Profile
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/my-applications"
                      className="block px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                    >
                      Applications
                    </Link>
                  </li>
                </>
              )}

              {/* Admin collapsible, placed after all other items */}
              {user && isScopeAdmin && scopeAdminPath && (
                <li className="pt-2">
                  <button
                    onClick={() => setAdminMobileOpen(v => !v)}
                    className="w-full flex items-center justify-between px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                  >
                    <span>Admin</span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${adminMobileOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {adminMobileOpen && (
                    <ul className="mt-1 ml-2 space-y-1">
                      {getSidebarItemsForRole(user.role).map((item) => (
                        <li key={`admin-mobile-${item.id}`}>
                          <Link
                            to={`${scopeAdminPath}#${item.id}`}
                            onClick={() => { setMobileMenuOpen(false); setAdminMobileOpen(false); }}
                            className="block px-5 py-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50"
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}
              {/* Admin section will be rendered after recruiter items */}
              {false && user && isScopeAdmin && scopeAdminPath && (<></>)}
              {user && isRecruiter && (
                <>
                  {/* Recruiter: Overview */}
                  <li>
                    <Link
                      to="/recruiter-dashboard#overview"
                      onClick={(e) => { if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } else { setMobileMenuOpen(false);} }}
                      className="block px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                    >
                      Overview
                    </Link>
                  </li>
                  {/* Recruiter: Hire section */}
                  <li>
                    <button
                      onClick={() => setRecHireOpen(!recHireOpen)}
                      className="w-full flex items-center justify-between px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                    >
                      <span>Hire</span>
                      <ChevronDown className={`w-5 h-5 transition-transform ${recHireOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {recHireOpen && (
                      <ul className="mt-1 ml-4 space-y-1">
                        <li>
                          <Link
                            to="/recruiter-dashboard#jobs"
                            onClick={(e) => { if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } else { setMobileMenuOpen(false);} }}
                            className="block px-4 py-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50"
                          >Jobs</Link>
                        </li>
                        <li>
                          <Link
                            to="/recruiter-dashboard#candidates"
                            onClick={(e) => { if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } else { setMobileMenuOpen(false);} }}
                            className="block px-4 py-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50"
                          >Candidates</Link>
                        </li>
                        <li>
                          <Link
                            to="/recruiter-dashboard#assessments"
                            onClick={(e) => { if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } else { setMobileMenuOpen(false);} }}
                            className="block px-4 py-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50"
                          >Assessments</Link>
                        </li>
                      </ul>
                    )}
                  </li>
                  {/* Recruiter: Retain section */}
                  <li>
                    <button
                      onClick={() => setRecRetainOpen(!recRetainOpen)}
                      className="w-full flex items-center justify-between px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                    >
                      <span>Retain</span>
                      <ChevronDown className={`w-5 h-5 transition-transform ${recRetainOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {recRetainOpen && (
                      <ul className="mt-1 ml-4 space-y-1">
                        <li>
                          <Link
                            to="/recruiter-dashboard#reviews"
                            onClick={(e) => { if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } else { setMobileMenuOpen(false);} }}
                            className="block px-4 py-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50"
                          >Reviews</Link>
                        </li>
                        <li>
                          <Link
                            to="/recruiter-dashboard#goals"
                            onClick={(e) => { if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } else { setMobileMenuOpen(false);} }}
                            className="block px-4 py-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50"
                          >Goals</Link>
                        </li>
                      </ul>
                    )}
                  </li>
                  {/* Recruiter: Settings section */}
                  <li>
                    <button
                      onClick={() => setRecSettingsOpen(!recSettingsOpen)}
                      className="w-full flex items-center justify-between px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                    >
                      <span>Settings</span>
                      <ChevronDown className={`w-5 h-5 transition-transform ${recSettingsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {recSettingsOpen && (
                      <ul className="mt-1 ml-4 space-y-1">
                        <li>
                          <Link
                            to="/recruiter-dashboard#general"
                            onClick={(e) => { if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } else { setMobileMenuOpen(false);} }}
                            className="block px-4 py-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50"
                          >General</Link>
                        </li>
                        <li>
                          <Link
                            to="/recruiter-dashboard#templates"
                            onClick={(e) => { if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } else { setMobileMenuOpen(false);} }}
                            className="block px-4 py-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50"
                          >Templates</Link>
                        </li>
                        <li>
                          <Link
                            to="/recruiter-dashboard#profile"
                            onClick={(e) => { if (recruiterBlocked) { e.preventDefault(); navigate('/waiting-approval'); } else { setMobileMenuOpen(false);} }}
                            className="block px-4 py-3 rounded-lg text-sm text-slate-700 hover:bg-blue-50"
                          >Profile</Link>
                        </li>
                      </ul>
                    )}
                  </li>
                </>
              )}
              {!isScopeAdmin && (
                <li>
                  <Link
                    to="/career-advice"
                    className="block px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                  >
                    Career Hub
                  </Link>
                </li>
              )}
              {!isScopeAdmin && (
                <li>
                  <Link
                    to="/explore-companies"
                    className="block px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                  >
                    Companies
                  </Link>
                </li>
              )}
              {isAdmin && (
                <>
                  <li>
                    <Link
                      to="/admin-dashboard"
                      className="block px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                    >
                      Admin
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/candidates"
                      className="block px-5 py-4 rounded-lg text-base text-slate-700 font-medium hover:bg-blue-50"
                    >
                      Talent Pool
                    </Link>
                  </li>
                </>
              )}
              <div className="border-t border-slate-200 my-3" />
              {user ? (
                <li>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-5 py-4 text-base text-red-600 font-medium hover:bg-red-50 rounded-lg"
                  >
                    Sign Out
                  </button>
                </li>
              ) : (
                <li>
                  <Link
                    to="/login"
                    className="relative block px-5 py-4.5 text-center rounded-xl text-base font-bold text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-700 via-blue-600 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 animate-pulse"></div>
                    <div className="relative flex items-center justify-center">
                      <User className="inline-block mr-2 w-5 h-5 group-hover:animate-bounce" />
                      Join Now
                    </div>
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;